import React, { useState } from 'react';
import useProfileData from '../hooks/useProfileData';
import './ProfilePage.css';

const ProfilePage = () => {
  const {
    profileData,
    hasUnsavedChanges,
    loading,
    error,
    successMessage,
    updateProfileFact,
    generateAddressOptions,
    saveProfileChanges,
    undoChanges,
    addPersonalInfoFact,
    removePersonalInfoFact,
    updatePersonalInfoFact,
    updateLearnedInformationFact,
    removeLearnedInformationFact,
    setError,
    setSuccessMessage
  } = useProfileData();

  const [newPersonalFact, setNewPersonalFact] = useState('');
  const [editingFactIndex, setEditingFactIndex] = useState(null);
  const [editingFactValue, setEditingFactValue] = useState('');
  const [editingLearnedIndex, setEditingLearnedIndex] = useState(null);
  const [editingLearnedValue, setEditingLearnedValue] = useState('');

  const handleSave = async () => {
    console.log('Save button clicked, hasUnsavedChanges:', hasUnsavedChanges);
    console.log('Current profileData:', profileData);
    const success = await saveProfileChanges();
    console.log('Save result:', success);
    if (success) {
      setError(null);
    }
  };

  const handleUndo = async () => {
    await undoChanges();
    setError(null);
    setSuccessMessage(null);
  };

  const handleAddPersonalFact = () => {
    if (newPersonalFact.trim()) {
      addPersonalInfoFact(newPersonalFact.trim());
      setNewPersonalFact('');
    }
  };

  const handleEditPersonalFact = (index) => {
    setEditingFactIndex(index);
    setEditingFactValue(profileData.personal_info[index].fact);
  };

  const handleSavePersonalFact = () => {
    if (editingFactValue.trim()) {
      updatePersonalInfoFact(editingFactIndex, editingFactValue.trim());
    }
    setEditingFactIndex(null);
    setEditingFactValue('');
  };

  const handleCancelEdit = () => {
    setEditingFactIndex(null);
    setEditingFactValue('');
  };

  // Learned information edit handlers (mirror Personal Info pattern)
  const handleEditLearnedFact = (index) => {
    setEditingLearnedIndex(index);
    setEditingLearnedValue(profileData.learned_information[index].fact);
  };

  const handleSaveLearnedFact = () => {
    if (editingLearnedValue.trim()) {
      updateLearnedInformationFact(editingLearnedIndex, editingLearnedValue.trim());
    }
    setEditingLearnedIndex(null);
    setEditingLearnedValue('');
  };

  const handleCancelLearnedEdit = () => {
    setEditingLearnedIndex(null);
    setEditingLearnedValue('');
  };

  const addressOptions = generateAddressOptions();

  return (
    <div className="profile-page">
      <div className="profile-content">
        <div className="profile-header">
          <h1 className="profile-title">
            User Profile
          </h1>
          <div className="profile-icon-container">
            <img 
              src="/images/jj_icon_character_profilepicture_reverse.png" 
              alt="Jeeves Junior Profile" 
              className="profile-icon"
            />
          </div>
        </div>

        {/* Instructional Text */}
        <h2 className="instructional-text">
          <div>Jeeves Junior can serve you better if he learns more about you.</div>
          <div>User Profile information is not saved until you click the Save Changes button at the bottom of the screen.</div>
        </h2>
        
        <div className="profile-section">
          <div className="profile-text">
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="success-message">
                {successMessage}
              </div>
            )}

            {/* Forms of Address Section */}
            <div className="profile-subsection">
              <h2 className="subsection-title">Forms of Address</h2>
              
              <div className="forms-row">
                <div className="form-group compact">
                  <label htmlFor="firstname">First Name:</label>
                  <input
                    id="firstname"
                    type="text"
                    value={profileData.firstname}
                    onChange={(e) => updateProfileFact('firstname', e.target.value)}
                    placeholder="Enter your first name"
                    className="form-input"
                  />
                </div>

                <div className="form-group compact">
                  <label htmlFor="lastname">Last Name:</label>
                  <input
                    id="lastname"
                    type="text"
                    value={profileData.lastname}
                    onChange={(e) => updateProfileFact('lastname', e.target.value)}
                    placeholder="Enter your last name"
                    className="form-input"
                  />
                </div>

                <div className="form-group compact">
                  <label htmlFor="gender">Gender:</label>
                  <select
                    id="gender"
                    value={profileData.gender}
                    onChange={(e) => updateProfileFact('gender', e.target.value)}
                    className="form-select"
                  >
                    <option value="Not Specified">Not Specified</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div className="form-group compact">
                  <label htmlFor="form_of_address">Preferred Form of Address:</label>
                  <select
                    id="form_of_address"
                    value={profileData.form_of_address}
                    onChange={(e) => updateProfileFact('form_of_address', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select preferred form of address</option>
                    {addressOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="profile-subsection">
              <h2 className="subsection-title">Share Personal Information</h2>
              <p className="subsection-description">
                Add personal facts that can help Junior provide more personalized responses.
              </p>

              {/* Add new personal fact */}
              <div className="add-fact-container">
                <input
                  type="text"
                  value={newPersonalFact}
                  onChange={(e) => setNewPersonalFact(e.target.value)}
                  placeholder="Add a personal fact about yourself..."
                  className="form-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddPersonalFact()}
                />
                <button
                  onClick={handleAddPersonalFact}
                  disabled={!newPersonalFact.trim()}
                  className="add-button"
                >
                  Add Fact
                </button>
              </div>

              {/* Personal facts list */}
              <div className="personal-facts-list">
                {profileData.personal_info.map((fact, index) => (
                  <div key={index} className="personal-fact-item">
                    {editingFactIndex === index ? (
                      <div className="edit-fact-container">
                        <input
                          type="text"
                          value={editingFactValue}
                          onChange={(e) => setEditingFactValue(e.target.value)}
                          className="form-input"
                          onKeyPress={(e) => e.key === 'Enter' && handleSavePersonalFact()}
                        />
                        <button onClick={handleSavePersonalFact} className="save-button">
                          Accept
                        </button>
                        <button onClick={handleCancelEdit} className="cancel-button">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="fact-content">
                        <span className="fact-text">{fact.fact}</span>
                        <div className="fact-actions">
                          <button
                            onClick={() => handleEditPersonalFact(index)}
                            className="edit-button"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removePersonalInfoFact(index)}
                            className="delete-button"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {profileData.personal_info.length === 0 && (
                <div className="no-facts-message">
                  No personal facts added yet. Add some facts about yourself to help the AI provide more personalized responses.
                </div>
              )}
            </div>

            {/* Learned Information Section */}
            <div className="profile-subsection">
              <h2 className="subsection-title">Learned Information</h2>
              {profileData.learned_information && profileData.learned_information.length > 0 && (
                <p className="subsection-description">
                  Jeeves Jr. will learn information about you as you chat. Personal information about you will appear here as it is detected.
                </p>
              )}

              {/* Learned facts list */}
              <div className="personal-facts-list">
                {profileData.learned_information.map((fact, index) => (
                  <div key={index} className="personal-fact-item">
                    {editingLearnedIndex === index ? (
                      <div className="edit-fact-container">
                        <input
                          type="text"
                          value={editingLearnedValue}
                          onChange={(e) => setEditingLearnedValue(e.target.value)}
                          className="form-input"
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveLearnedFact()}
                        />
                        <button onClick={handleSaveLearnedFact} className="save-button">
                          Accept
                        </button>
                        <button onClick={handleCancelLearnedEdit} className="cancel-button">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="fact-content">
                        <span className="fact-text">{fact.fact}</span>
                        <div className="fact-actions">
                          <button
                            onClick={() => handleEditLearnedFact(index)}
                            className="edit-button"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeLearnedInformationFact(index)}
                            className="delete-button"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {(!profileData.learned_information || profileData.learned_information.length === 0) && (
                <div className="no-facts-message">
                  Jeeves Jr. will learn information about you as you chat. Personal information about you will appear here as it is detected.
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                onClick={handleSave}
                disabled={loading || !hasUnsavedChanges}
                className="save-button primary"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleUndo}
                disabled={loading || !hasUnsavedChanges}
                className="undo-button"
              >
                Undo Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
